import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiUrl } from '@refinedev/core';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Eye,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ─── Types ─────────────────────────────────────────────────────────

interface DeliveryRecord {
  id: number;
  userId: number;
  channel: string;
  status: 'SENT' | 'FAILED';
  error: string | null;
  sentAt: string | null;
  createdAt: string;
  firstOpenedAt: string | null;
  openCount: number;
  user: {
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    email: string | null;
    telegramId: number | null;
  };
}

interface DeliveriesResponse {
  data: DeliveryRecord[];
  total: number;
  page: number;
  limit: number;
}

interface TabDeliveriesProps {
  broadcastId: number;
  broadcastStatus: string;
}

// ─── Constants ─────────────────────────────────────────────────────

const LIMIT = 50;
const DEBOUNCE_MS = 500;
const EXPORT_LIMIT = 10000;
const ALL_STATUSES = 'ALL';

// ─── Component ─────────────────────────────────────────────────────

export function TabDeliveries({ broadcastId, broadcastStatus }: TabDeliveriesProps) {
  const apiUrl = useApiUrl();

  const [records, setRecords] = useState<DeliveryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const buildParams = useCallback((pageNum: number, limitNum: number, searchVal: string, status: string): URLSearchParams => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(limitNum),
    });
    if (searchVal) {
      params.set('search', searchVal);
    }
    if (status !== ALL_STATUSES) {
      params.set('status', status);
    }
    return params;
  }, []);

  const getAuthHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = buildParams(page, LIMIT, debouncedSearch, statusFilter);
      const res = await fetch(
        `${apiUrl}/admin/broadcasts/${broadcastId}/deliveries?${params}`,
        { headers: getAuthHeaders() },
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const json = await res.json() as DeliveriesResponse;
      setRecords(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось загрузить данные';
      setError(message);
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, broadcastId, page, debouncedSearch, statusFilter, buildParams, getAuthHeaders]);

  useEffect(() => {
    if (broadcastStatus === 'DRAFT') {
      return;
    }
    fetchDeliveries();
  }, [fetchDeliveries, broadcastStatus]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handlePrevPage = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  const formatUserName = (user: DeliveryRecord['user']): string => {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) {
      return '—';
    }
    return new Date(dateString).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const escapeCsvField = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replaceAll('"', '""')}"`;
    }
    return value;
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = buildParams(1, EXPORT_LIMIT, debouncedSearch, statusFilter);
      const res = await fetch(
        `${apiUrl}/admin/broadcasts/${broadcastId}/deliveries?${params}`,
        { headers: getAuthHeaders() },
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const json = await res.json() as DeliveriesResponse;
      const exportRecords = json.data ?? [];

      const header = 'ID,\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C,Username,Email,\u041A\u0430\u043D\u0430\u043B,\u0421\u0442\u0430\u0442\u0443\u0441,\u041E\u0448\u0438\u0431\u043A\u0430,\u0412\u0440\u0435\u043C\u044F\n';
      const rows = exportRecords.map((r) => {
        const name = `${r.user.firstName ?? ''} ${r.user.lastName ?? ''}`.trim();
        return [
          String(r.userId),
          escapeCsvField(name),
          r.user.username ?? '',
          r.user.email ?? '',
          r.channel,
          r.status,
          escapeCsvField(r.error ?? ''),
          r.sentAt ?? r.createdAt,
        ].join(',');
      }).join('\n');

      const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `broadcast-${broadcastId}-deliveries.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка экспорта';
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  // Draft state — empty
  if (broadcastStatus === 'DRAFT') {
    return (
      <div className="rounded-lg border-dashed border-2 p-12 text-center">
        <AlertTriangle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-lg font-medium text-muted-foreground">
          Рассылка ещё не отправлялась
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Отправьте рассылку, чтобы увидеть лог доставки
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar: search + filter + export */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по имени или username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>Все</SelectItem>
              <SelectItem value="SENT">Отправлено</SelectItem>
              <SelectItem value="FAILED">Ошибка</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={exportCSV}
          disabled={exporting || total === 0}
        >
          {exporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Экспорт CSV
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Пользователь</TableHead>
              <TableHead>Канал</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Открыто</TableHead>
              <TableHead>Ошибка</TableHead>
              <TableHead>Время</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            )}

            {!loading && records.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {debouncedSearch || statusFilter !== ALL_STATUSES
                    ? 'Ничего не найдено'
                    : 'Нет записей'}
                </TableCell>
              </TableRow>
            )}

            {!loading && records.map((record) => (
              <TableRow key={record.id}>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{formatUserName(record.user)}</p>
                    {record.user.username && (
                      <p className="text-xs text-muted-foreground">@{record.user.username}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {record.channel}
                  </Badge>
                </TableCell>
                <TableCell>
                  {record.status === 'SENT' ? (
                    <Badge variant="default" className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      SENT
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      FAILED
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {record.channel === 'EMAIL' && record.firstOpenedAt ? (
                    <Badge variant="default" className="bg-violet-500/10 text-violet-600 hover:bg-violet-500/20">
                      <Eye className="h-3 w-3 mr-1" />
                      {record.openCount > 1 ? `${record.openCount}×` : 'да'}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {record.error ? (
                    <span className="text-xs text-destructive">{record.error}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(record.sentAt ?? record.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Показано {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} из {total.toLocaleString('ru-RU')}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Назад
            </Button>
            <span className="text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={page >= totalPages}
            >
              Вперёд
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
